import cityConfig from '@/config/city';
import { useCurrencyStore } from '@/stores/currency';
import { map3piSourceFile } from '@/utils/map-3pi-export.js';

// COD-877: lazy map of the static 3PI exports; keys tell us which cities have one
// without import() message sniffing, and each JSON only loads when its loader runs.
// COD-1067: used only when static_enable3PICatalogApi is off.
let threePiExportModules;
function getThreePiExportModules() {
  threePiExportModules ??= import.meta.glob('@/mocks/3pi/*.json');
  return threePiExportModules;
}

export function useApiCities() {
  const {
    $api,
    $i18n,
  } = useNuxtApp();
  const { locale } = $i18n;

  const currencyStore = useCurrencyStore();
  const testingStore = useTestingStore();
  const dateTime = useDateTime();
  const { useImage } = useImages();

  const userCurrency = currencyStore.getCurrentCurrency;

  const defaultQuery = { currency: userCurrency?.code };

  const defaultHeaders = { 'Accept-Language': locale?.value };

  const defaultParams = {};
  const getBadgeIcon = (badgeName) => {
    switch (badgeName) {
      case 'Breakfast Included':
        return { icon: 'breakfast' };
      case 'Free WiFi':
        return { icon: 'wi-fi' };
      case 'Sanitisation Badge':
        return {
          icon: 'sanitation-badge',
          illustration: 'sanitation',
        };
      case 'hoscar':
        return {
          icon: 'trophy',
          illustration: 'trophy',
        };

      case 'experience':
        return {
          icon: 'rocket',
          illustration: 'rocket',
        };
    }
  };
  const updateApiCityProperties = (propertyList) => {
    return propertyList?.map((property) => {
      property.description = property.intro;
      property.rating = property.avgRating;
      property.numReviews = property.numberReviews;
      property.distance = property.cityCenterDistance;
      if (property.badges && Array.isArray(property.badges)) {
        property.badges = property.badges.map((badge) => {
          const badgeGraphics = getBadgeIcon(badge.badgeName);
          badge.name = badge.badgeName;
          badge.icon = badgeGraphics.icon;
          badge.illustration = badgeGraphics.illustration;
          badge.showLabel = badge.displayMessage;
          return badge;
        });
      }
      property.minDormPrice = property.sharedMinPrice;
      property.minPrivatePrice = property.privateMinPrice;
      property.urlPath = `hosteldetails.php/${property.urlFriendlyName}/${property.urlFriendlyCity}/${property.id}`;
      property.imgSizes = property.imageSizes?.propertyList;
      property.latitude = property.geoCoordinates?.latitude || null;
      property.longitude = property.geoCoordinates?.longitude || null;
      return property;
    });
  };

  const getCityById = async (cityId) => {
    const callKey = import.meta.client ? null : `getCityById${locale?.value}${cityId}${userCurrency?.code}`;
    const endpoint = `city/${cityId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));
    return result?.value?.data || null;
  };

  const getCityByCityNameAndCountryName = async (countryName, cityName) => {
    let result = ref(null);

    const callKey = `getCityByCityNameAndCountryName${locale?.value}${countryName}${cityName}${userCurrency?.code}`;
    const endpoint = `city/${cityName}/${countryName}/`;

    result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));
    return result?.value?.data || null;
  };

  const getCityReviewSummaries = async (cityId) => {
    let result = ref(null);

    const callKey = `getCityReviewsSummary${locale?.value}${cityId}${userCurrency?.code}`;
    const endpoint = `city-reviews/${cityId}/summary/`;

    result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));

    return {
      reviews: result?.value?.data?.reviews,
      ratings: result?.value?.data?.ratings,
    };
  };

  const getPropertiesLinkupsApiCall = (properties, page) => {
    if (!properties?.length) {
      return null;
    }

    const propertyIdsByComma = properties?.map((property) => property.id).join(',');

    const linkupsVars = testingStore.getFeatureVariables('web_linkupsCallouts');
    const linkupsNumDays = linkupsVars?.numDays || 7;
    const callKeyLinkupsApi = `getCityPropertyLinkupsEvents${locale?.value}${userCurrency?.code}`;
    const endpointLinkupsApi = 'public/properties/hostel-gatherings/stats';

    const queryLinksupsApi = {
      page,
      propertyIds: propertyIdsByComma,
      startDate: dateTime.formatToFlat(dateTime.today),
      endDate: dateTime.formatToFlat(dateTime.addDaysToDate(dateTime.today, linkupsNumDays)),
    };
    return {
      key: 'linkups',
      apiCall: () =>
        $api.linkupsApi(callKeyLinkupsApi, endpointLinkupsApi, defaultHeaders, queryLinksupsApi, defaultParams),
    };
  };

  const getPropertiesSocialCuesApiCall = (properties) => {
    if (!properties?.length) {
      return null;
    }

    const propertyIdsByComma = properties?.map((property) => property.id).join(',');
    const socialCuesStartDate = dateTime.tomorrow;
    const socialCuesEndDate = dateTime.addDaysToDate(socialCuesStartDate, 3);
    const callKeySocialCuesApi = `getPropertySocialCues${locale?.value}${userCurrency?.code}`;
    const endpointSocialCuesApi = 'social-cues/multiple-properties/';
    const querySocialCuesApi = {
      property_ids: propertyIdsByComma,
      from: dateTime.formatToFlat(socialCuesStartDate, 'dd-MM-yyyy'),
      to: dateTime.formatToFlat(socialCuesEndDate, 'dd-MM-yyyy'),
      number_of_results: cityConfig.socialCues.limitAvatars,
    };
    return {
      key: 'socialcues',
      apiCall: () =>
        $api.tpapi(callKeySocialCuesApi, endpointSocialCuesApi, defaultHeaders, querySocialCuesApi, defaultParams),
    };
  };

  // @TODO: https://hostelworld.atlassian.net/browse/COD-5 -> API Source needs to change when apigee work in on SPAPI
  const getCityProperties = async (
    propertyType,
    cityId,
    pageNum = 1,
    limiter = 30,
    isFeatured = 0,
    filters = null,
    cityContext = null,
  ) => {
    const defaultPaginationValue = 1;
    const page = Number.parseInt(pageNum) || defaultPaginationValue;
    const featured = Number.parseInt(isFeatured) || 0;
    const limit = Number.parseInt(limiter) || 30;

    // COD-751 / COD-1067: display flag merges 3PI; catalog flag chooses JSON mocks vs static catalog.
    const isEnable3PIProperties = testingStore.hasEnabledFeature('static_enable3PIProperties');
    const isEnable3PICatalogApi = testingStore.hasEnabledFeature('static_enable3PICatalogApi');
    const experimentNewSortOrder = testingStore.hasEnabledFeature('web_newSortOrder');
    const experimentNewSortOrderParticipating = testingStore.hasEnabledFeature('web_newSortOrderIsUserParticipating');
    const experimentSocialCuesParticipating = testingStore.hasEnabledFeature(
      'web_isUserParticipatingSocialCuesCallouts',
    );
    const bucketNewSortOrder = experimentNewSortOrder ? 'variation' : 'control';

    const internalQuery = {
      limit,
      page,
      featured,
      filters,
      ...defaultQuery,
      origin: 'spapi',
      v: experimentNewSortOrderParticipating ? bucketNewSortOrder : 'holdout',
      priorityPropertyType: propertyType,
    };

    const callKeySpapi = `getCityProperties${locale?.value}${propertyType}${cityId}${page}${featured}${limit}${filters}${userCurrency?.code}`;
    const endpointSpapi = `city/${propertyType}/${cityId}/properties/`;
    const normalizedCityId = cityId ? Number.parseInt(cityId, 10) : Number.NaN;
    const hasValidCityId = Number.isInteger(normalizedCityId) && normalizedCityId > 0;
    const callKeyCatalog = `staticCatalog3pi:${locale?.value}:${normalizedCityId}:${limit}:${userCurrency?.code}`;
    // COD-1067: SSR catalog; start in parallel with SPAPI and skip on featured to avoid a second catalog call on the city page.
    const catalogPromise = hasValidCityId && isEnable3PICatalogApi && !featured
      ? Promise.resolve().then(() =>
        $api.staticCatalogApi(
          callKeyCatalog,
          'static-pages/3pi/cities/properties',
          {},
          {
            hwCityId: normalizedCityId,
            pageSize: limit,
          },
          {},
        ),
      )
      : null;

    const resultSpapi = ref(
      await $api.apigee(callKeySpapi, endpointSpapi, defaultHeaders, internalQuery, defaultParams),
    );
    const spapiData = resultSpapi?.value?.data || {};

    if (spapiData?.properties?.length && !isFeatured) {
      const extraApiCalls = [];

      const linkupsApiCall = getPropertiesLinkupsApiCall(spapiData.properties, page);
      if (linkupsApiCall) {
        extraApiCalls.push(linkupsApiCall);
      }

      if (experimentSocialCuesParticipating) {
        // SOCIAL CUES API CALL DATA
        const socialCuesApiCall = getPropertiesSocialCuesApiCall(spapiData.properties);
        if (socialCuesApiCall) {
          extraApiCalls.push(socialCuesApiCall);
        }
      }

      if (extraApiCalls.length) {
        // CALL ALL OTHER NECESSARY APIs AT THE SAME TIME AND WAIT FOR THE SLOWEST ONE...
        const extraApiCallsResults = await Promise.all(extraApiCalls.map((c) => c.apiCall()));
        // ... AND THEN MAP THE RESULTS TO THE CORRECT API CALL
        const extraApiCallsData = extraApiCallsResults.reduce((acc, result, index) => {
          acc[extraApiCalls[index].key] = toValue(result) || {};
          return acc;
        }, {});

        const propertiesLinkupsApiInfo = extraApiCallsData?.linkups || [];
        const propertiesSocialCuesApiInfo = extraApiCallsData?.socialcues || [];

        spapiData.properties = spapiData.properties?.map((property) => {
          if (propertiesSocialCuesApiInfo.length) {
            const propertySocialCues = propertiesSocialCuesApiInfo.find((item) => item.propertyId === property.id);
            property.whosGoing
              = propertySocialCues?.total > 0
                ? {
                  users:
                      propertySocialCues?.data?.map((user) => ({
                        picture: useImage.getOptimizedImageUrl(user?.profilePictureUrl, {
                          width: 32,
                          height: 32,
                        }),
                        flag: user?.countryCode ? `https://a.hwstatic.com/hw/flags/${user.countryCode}.svg` : null,
                        name: '',
                      })) || [],
                  total: propertySocialCues?.total,
                }
                : {};
          }
          if (propertiesLinkupsApiInfo.length) {
            const propertyLinkups = propertiesLinkupsApiInfo.find((item) => item.propertyId === property.id);
            property.linkups = propertyLinkups || {};
          }
          return property;
        });
      }
    }

    const cityPropertiesData = {
      totalPropertiesCount: spapiData?.totalPropertiesCount || 0,
      numberOfPages: spapiData?.numberOfPages || 1,
      page: spapiData?.page || 1,
      properties: updateApiCityProperties(spapiData.properties) || [],
    };

    // COD-877: 3PI meta for tracking; resolved for both cohorts so 3pi_city_flag stays true in control.
    const threePiData = {
      isExperimentCity: false,
      estimatedCount: 0,
      totalCount: 0,
      hwCount: cityPropertiesData.totalPropertiesCount,
      properties: [],
      hasError: false,
    };

    const applyThreePiExport = (threePiExport) => {
      const threePiProperties = threePiExport?.properties;

      if (!Array.isArray(threePiProperties) || !threePiProperties.length) {
        return;
      }

      threePiData.isExperimentCity = true;
      threePiData.totalCount = threePiExport.totalPropertiesCount ?? threePiProperties.length;
      threePiData.estimatedCount = threePiExport.estimatedPropertiesCount || threePiData.totalCount;

      if (!isEnable3PIProperties) {
        return;
      }

      cityPropertiesData.totalPropertiesCount += threePiData.totalCount;

      if (page === 1 && !featured) {
        threePiData.properties = threePiProperties;
        cityPropertiesData.properties = [...cityPropertiesData.properties, ...threePiProperties];
      }
    };

    if (cityId) {
      if (catalogPromise) {
        try {
          const catalogResponse = await catalogPromise;

          if (catalogResponse == null) {
            threePiData.hasError = true;
            console.error('### Failed to load 3PI properties - cityId:', normalizedCityId);
          } else if (catalogResponse.status !== 'Empty' && catalogResponse.properties?.length) {
            const cityInfo = cityContext || (await getCityById(normalizedCityId));

            if (!cityInfo?.name || !cityInfo?.country || !cityInfo?.urlFriendlyName || !cityInfo?.urlFriendlyCountry) {
              threePiData.hasError = true;
              console.error('### Failed to resolve city context for 3PI - cityId:', normalizedCityId);
            } else {
              applyThreePiExport(
                map3piSourceFile({
                  cityId: normalizedCityId,
                  city: cityInfo.name,
                  country: cityInfo.country,
                  slug: `${cityInfo.urlFriendlyName}/${cityInfo.urlFriendlyCountry}`,
                  apiMeta: {
                    currency: catalogResponse.currency,
                    status: catalogResponse.status,
                    expectedCount: catalogResponse.expectedCount,
                    total: catalogResponse.total,
                  },
                  properties: catalogResponse.properties,
                }),
              );
            }
          }
        } catch (err) {
          threePiData.hasError = true;
          console.error('### Failed to load 3PI properties - cityId:', normalizedCityId, ':', err);
        }
      } else if (hasValidCityId && !isEnable3PICatalogApi) {
        const exportModules = getThreePiExportModules();
        const exportKey = Object.keys(exportModules).find((key) =>
          key.endsWith(`/${normalizedCityId}.json`),
        );

        if (exportKey) {
          try {
            const threePiModule = await exportModules[exportKey]();
            applyThreePiExport(threePiModule?.default ?? threePiModule);
          } catch (err) {
            // Only reachable when the export exists but failed to load; fires the Third Party Error Event.
            threePiData.hasError = true;
            console.error('### Failed to load the 3PI export:', err);
          }
        }
      }
    }

    cityPropertiesData.threePi = threePiData;

    return cityPropertiesData || null;
  };

  const getCitySocialCues = async (cityId) => {
    const experimentSocialCuesCityBlockParticipating = testingStore.hasEnabledFeature(
      'web_socialCuesCalloutsCityBlock_isParticipating',
    );

    if (!experimentSocialCuesCityBlockParticipating) {
      return null;
    }

    const startOfNextWeek = dateTime.addDaysToDate(dateTime.today, 7 - dateTime.getDayOfWeek(dateTime.today));
    const endOfNextWeek = dateTime.addDaysToDate(startOfNextWeek, 6);

    const callKey = `getCitySocialCues${locale?.value}${cityId}`;
    const endpoint = 'guest-counts/';

    const citySocialCuesQuery = {
      cityId,
      startDate: dateTime.formatToFlat(startOfNextWeek),
      endDate: dateTime.formatToFlat(endOfNextWeek),
    };

    const result = ref(await $api.bsapi(callKey, endpoint, defaultHeaders, citySocialCuesQuery, defaultParams));
    return result?.value || null;
  };

  const getCountryTopCities = async (countryId) => {
    let result = ref(null);

    const callKey = `getCountryTopCities${locale?.value}${countryId}${userCurrency?.code}`;
    const endpoint = `top/cities/${countryId}/`;

    result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));
    return result?.value?.data?.cities || null;
  };

  const getCityDistricts = async (propertyType, cityId) => {
    let result = ref(null);

    const callKey = `getCityDistricts${locale?.value}${propertyType}${cityId}${userCurrency?.code}`;
    const endpoint = `city/${propertyType}/${cityId}/districts/`;

    result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));
    return result?.value?.data?.districts || null;
  };

  const getNearestCityByCoordinates = async (latitude, longitude) => {
    let result = ref(null);

    const callParams = {
      ...defaultParams,
      latitude,
      longitude,
    };

    const callKey = `getNearestCityByCoordinates${locale?.value}${latitude}${longitude}`;
    const endpoint = 'nearest-city/';

    result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, callParams));
    return result?.value?.data || null;
  };

  const getTopPropertiesByCityId = async (propertyType, cityId) => {
    let result = ref(null);
    const callKey = `getTopPropertiesByCityId${locale?.value}${propertyType}${cityId}${userCurrency?.code}`;
    const endpoint = `top/properties/${propertyType}/${cityId}/`;

    result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));

    return updateApiCityProperties(result?.value?.data?.properties) || null;
  };

  const getTopPropertiesByCityIdPerFilters = async (propertyType, cityId) => {
    let result = ref(null);

    const query = {
      ...defaultQuery,
      limit: 16, // Set a default limit for the number of properties per filter
    };

    const callKey = `getTopPropertiesByCityIdPerFilters${locale?.value}${propertyType}${cityId}${userCurrency?.code}`;
    const endpoint = `top/properties/${propertyType}/${cityId}/filters/`;

    result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, query, defaultParams));
    return result?.value?.data.filters || null;
  };

  return {
    getCityById,
    getCityByCityNameAndCountryName,
    getCityProperties,
    getCityDistricts,
    getCountryTopCities,
    getCityReviewSummaries,
    getCitySocialCues,
    getNearestCityByCoordinates,
    getTopPropertiesByCityId,
    getTopPropertiesByCityIdPerFilters,
  };
}
